import React, { useEffect, useState } from "react"
import { Card, CardBody } from "reactstrap"
import { useHistory } from "react-router-dom"
import { useSelector } from "react-redux"

import {
   DeleteTimelineOrder,
   GetProductionLinesListing,
   GetTimelineOrderListing,
} from "api/api.service"
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import { DateRangePicker } from "components/DatePicker"
import { AutoComplete } from "components/AutoComplete"
import { Pagination } from "components/Pagination"
import { DataTable } from "components/DataTable"
import { Refresh } from "components/Refresh"
import { TextInput } from "components/TextInput"
import { Modal } from "components/Modal"

import EditIcon from "../../../assets/images/icons/edit-icon.svg"
import DeleteIcon from "assets/images/icons/finance-vendor-mang-del-icon.svg"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import ActionEyeIcon from "assets/images/icons/feather-eye-icon.png"
import { SearchBox } from "components/SearchBox"
import { Button } from "components/Button"
import { AddTrailerModal } from "../Modals"
import BulkUpdateProductionLineModal from "../Modals/BulkUpdateProductionLineModal"
import useAsyncSelect from "hooks/useAsyncSelect"
import moment from "moment"
import { TimeLineOrderStatus } from "constants"
import { APP_ROUTES, getRoute } from "helpers/routeHelpers"
import { updateObjCheckBox } from "utils/commonUtils"
import DeleteModal from "pages/Configurator/CommonUI/Modal/DeleteModal"

export const calculateTrailerStatus = data => {
   const currentDate = moment().utc().startOf("day")

   if (
      data?.estimatedDate &&
      moment(data.estimatedDate).utc().isBefore(currentDate)
   ) {
      return TimeLineOrderStatus.COMPLETED
   }

   if (data?.timelineCells?.[0]?.date) {
      const timelineDate = moment(data.timelineCells[0].date)
         .utc()
         .startOf("day")

      if (timelineDate.isSameOrBefore(currentDate)) {
         return TimeLineOrderStatus.IN_PROGRESS
      }
   }

   return TimeLineOrderStatus.OPEN
}

const COLUMNS = {
   SELECT_ALL: "Select All",
   SLOT_ID: "Slot Id",
   TRAILER_TYPE: "Trailer Type",
   CUSTOMER_NAME: "Customer Name",
   PRODUCTION_LINE: "Production Line",
   DATE_ADDED: "Date Added",
   ESTIMATED_DATE: "Estimated Date",
   PRIORITY: "Priority",
   STATUS: "Status",
   ACTION: "Action",
}
const DATA_KEY = "data"
const TOTAL_KEY = "count"
const UNLIMITED_PRODUCTION_LINE_LIMIT = -1

const statusSelectOptions = [
   {
      label: "Open",
      value: "OPEN",
   },
   {
      label: "In Progress",
      value: "IN_PROGRESS",
   },
   {
      label: "Completed",
      value: "COMPLETED",
   },
]

const initialRangeValue = [
   {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
   },
]

const AllTrailers = () => {
   let controller = new AbortController()
   let signal = controller.signal
   const [delId, setDelId] = useState(null)
   const history = useHistory()
   const productionSchedules = useSelector(
      state => state?.productionSchedules?.productionSchedules
   )
   const hasProductionSchedules =
      Array.isArray(productionSchedules) && productionSchedules.length > 0
   const [productionFilter, setProductionFilter] = useState(null)
   const [range, setRange] = useState(initialRangeValue)
   const [hasUserSelectedDate, setHasUserSelectedDate] = useState(false)
   const [statusFilters, setStatusFilters] = useState(null)
   const [modalToggle, setModalToggle] = useState(false)
   const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false)
   const [selection, setSelection] = useState({})
   const [currentTrailerItem, setCurrentTrailerItem] = useState(null)
   const [selectedTrailers, setSelectedTrailers] = useState([])
   const [isSelectAll, setIsSelectAll] = useState(false)
   const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false)
   const [openDelModal, setOpenDelModal] = useState(false)

   const {
      selectEntity: productionListing,
      handleSearchOnOptions,
      handleSelectAOption,
   } = useAsyncSelect({
      apiFunc: GetProductionLinesListing,
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
      labelKey: "name",
      valueKey: "id",
      deps: [],
      params: {
         limit: UNLIMITED_PRODUCTION_LINE_LIMIT,
      },
      attachSignal: true,
   })

   const params = {
      ...(!!productionFilter?.id && { productionLineId: productionFilter?.id }),
      ...(statusFilters?.value && { status: statusFilters?.value }),
      ...getDateRangeParams(range[0]),
   }

   function getDateRangeParams(dateRange) {
      if (!hasUserSelectedDate) return {}
      const params = {}
      if (dateRange.startDate) {
         params.dateFrom = moment(dateRange.startDate)
            .utc()
            .startOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      if (dateRange.endDate) {
         params.dateTo = moment(dateRange.endDate)
            .utc()
            .endOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      return params
   }

   const {
      data,
      page,
      pageSize,
      total,
      isFetching,
      searchText,
      request,
      handleSearchChange,
      handlePageClick,
      onChangePageSize,
   } = useLocalPaginatedRequest({
      requestFn: GetTimelineOrderListing,
      params,
      deps: [productionFilter, range, statusFilters],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
   })

   // Update select all state when data or selectedTrailers changes
   useEffect(() => {
      if (!data || !Array.isArray(data)) return

      const currentPageItems = data.filter(el => el?.id)
      const selectedCurrentPageItems = selectedTrailers.filter(item =>
         currentPageItems.some(currentItem => currentItem.id === item.id)
      )
      setIsSelectAll(
         selectedCurrentPageItems.length === currentPageItems.length &&
            currentPageItems.length > 0
      )
   }, [data, selectedTrailers])

   const handleSearch = (value = "") => {
      handleSearchOnOptions(value)
   }

   const handleModalToggle = data => {
      setSelectedData(data || {})
      setModalToggle(prev => !prev)
   }
   const handleSelectStatusOption = option => {
      setStatusFilters(option)
   }

   const handleSetProduction = option => {
      if (typeof option !== "object" || !option) {
         setProductionFilter(null)
         return
      }
      setProductionFilter(option)
      setRange(initialRangeValue)
      handleSelectAOption(option, option.id)
   }

   const handleDateChange = ({ selection }) => {
      const { startDate, endDate } = selection
      let safeEndDate = endDate
      if (!(endDate instanceof Date) || isNaN(endDate)) {
         safeEndDate = startDate
      }

      const adjustedSelection = {
         ...selection,
         endDate: safeEndDate,
      }

      setRange([adjustedSelection])
      setHasUserSelectedDate(true)
   }

   const handleNavigateToProductionSchedule = data => {
      history.push(APP_ROUTES.TIMELINE_PRODUCTION_SCHEDULE, {
         id: data?.id,
         productionLineId: data?.productionLineId,
      })
   }
   const handleClearDateRange = setIsOpen => {
      setIsOpen(false)
      setRange(initialRangeValue)
      setHasUserSelectedDate(false)
   }

   // Checkbox selection handlers
   const handleCheckBoxSelect = (e, item) => {
      const dataTemp = updateObjCheckBox(item, selectedTrailers, "id")
      let filteredData = data.filter(el => el?.id) // Assuming all items have an id

      isSelectAll &&
         dataTemp.length !== filteredData.length &&
         setIsSelectAll(false)
      !isSelectAll &&
         dataTemp.length === filteredData.length &&
         setIsSelectAll(true)
      setSelectedTrailers(dataTemp)
   }

   // Select all handler
   const handleSelectAll = () => {
      let filteredData = data.filter(el => el?.id)
      isSelectAll ? setSelectedTrailers([]) : setSelectedTrailers(filteredData)
      setIsSelectAll(!isSelectAll)
   }

   // Bulk update production line handler
   const handleBulkUpdateProductionLine = () => {
      if (selectedTrailers.length === 0) return
      setIsBulkUpdateModalOpen(true)
   }

   // Handle bulk update modal close
   const handleBulkUpdateModalClose = () => {
      setIsBulkUpdateModalOpen(false)
   }

   const handleRefresh = () => {
      // Clear selection state when refreshing
      setSelectedTrailers([])
      setIsSelectAll(false)
      request()
   }

   const handleInitDelete = obj => {
      setDelId(obj.id)
      setOpenDelModal(prev => !prev)
   }

   return (
      <div className="pb-100">
         <Card className="mb-10">
            <Refresh onClick={handleRefresh} />
            <CardBody className="p-2 p-md-4 pt-0 pt-md-0">
               <div className="mb-2">
                  <div className="d-flex align-items-end justify-content-end admin-inventory-request-form-fields flex-column gap-1">
                     <div className="d-flex justify-content-start align-items-center admin-inventory-request-autocomplete gap-2">
                        {hasProductionSchedules && (
                           <div
                              className="text-danger mb-1"
                              style={{
                                 fontSize: "12px",
                              }}
                           >
                              Can't create/update order or move trailer right
                              now because a rescheduling process is going on.
                           </div>
                        )}

                        <div>
                           <Button
                              className="header-button"
                              title="Move Trailer"
                              onClick={handleBulkUpdateProductionLine}
                              disabled={
                                 selectedTrailers?.length === 0 ||
                                 hasProductionSchedules
                              }
                           />
                        </div>
                        <div>
                           <Button
                              className={`header-button headerButtonResponsive  `}
                              title="Create Order"
                              onClick={() =>
                                 setIsTrailerModalOpen(!isTrailerModalOpen)
                              }
                              disabled={hasProductionSchedules}
                           />
                        </div>

                        <div className="min-250-w margin-top-handle">
                           <SearchBox
                              inputClasses="vendor-search-input input-search"
                              labelClass="d-none"
                              placeholder="Search"
                              searchIcon={true}
                              searchIconClass="vendor-table-search-icon"
                              conClass="vendor-table-search-inner-class"
                              searchText={searchText}
                              onChange={handleSearchChange}
                           />
                        </div>
                     </div>
                  </div>
                  <div className="d-flex align-items-start justify-content-end admin-inventory-request-form-fields flex-column gap-1 mt-3">
                     <div className="d-flex justify-content-end align-items-end admin-inventory-request-autocomplete gap-2">
                        <div className={`min-250-w margin-top-handle`}>
                           <AutoComplete
                              value={statusFilters}
                              isClearable
                              onChange={val => handleSelectStatusOption(val)}
                              isSearchable={false}
                              placeholder="Select Status"
                              customStyles={autoCompleteStyles.STYLE_SIX}
                              classNamePrefix="status-header-search-admin"
                              options={statusSelectOptions}
                              menuPortalTarget={document.body}
                           />
                        </div>
                        <div className="min-250-w d-flex">
                           <DateRangePicker
                              range={range}
                              onChange={handleDateChange}
                              onClear={handleClearDateRange}
                              showClearIcon={true}
                              hasUserSelectedDate={hasUserSelectedDate}
                           />
                        </div>
                        <div className={"min-250-w mr-handle"}>
                           <AutoComplete
                              placeholder="Select Production Line"
                              isClearable
                              value={productionFilter}
                              customStyles={autoCompleteStyles.STYLE_SIX}
                              onInputChange={val => handleSearch(val)}
                              options={productionListing?.data}
                              onChange={val => handleSetProduction(val)}
                              closeMenuOnSelect={true}
                              isLoading={productionListing?.fetching}
                              classNamePrefix="status-header-search-admin"
                              menuPortalTarget={document.body}
                           />
                        </div>
                     </div>
                  </div>
               </div>
               <DataTable
                  data={data}
                  loading={isFetching}
                  tableClasses="ad-inventory-request-table"
                  config={[
                     {
                        title: COLUMNS.SELECT_ALL,
                        selectAll: true,
                        selectAllClickHandler: handleSelectAll,
                        isSelectAll: isSelectAll,
                        thClass: "checkbox-header",
                        tdClass: "checkbox-column",
                        className: "checkbox-column",
                        isDisabled: !data?.length,
                        render: data => {
                           return (
                              <TextInput
                                 onClick={e => handleCheckBoxSelect(e, data)}
                                 labelClass="mb-0 "
                                 type="checkbox"
                                 inputClass="form-check-input-custom fm-table-checkboxes"
                                 checked={
                                    selectedTrailers.length > 0
                                       ? selectedTrailers.filter(
                                            el => el.id === data.id
                                         ).length > 0
                                       : false
                                 }
                                 disabled={!data?.id}
                              />
                           )
                        },
                     },
                     {
                        title: COLUMNS.SLOT_ID,
                        render: data => {
                           return (
                              <div className="highlighted-col">
                                 {data?.timelineCells?.[0]?.slotId}
                              </div>
                           )
                        },
                     },
                     {
                        title: COLUMNS.CUSTOMER_NAME,
                        render: data => data?.customer?.name || "",
                     },
                     {
                        title: COLUMNS.PRODUCTION_LINE,
                        render: data => data?.productionLine?.name || "-",
                     },
                     {
                        title: COLUMNS.TRAILER_TYPE,
                        render: data => data?.trailer?.trailerType,
                     },
                     {
                        title: COLUMNS.DATE_ADDED,
                        render: data =>
                           data.createdAt
                              ? convertDateTime({
                                   date: data.createdAt,
                                   customFormat: dateTimeFormat.americanFormat,
                                   dateOnly: true,
                                })
                              : "-",
                     },
                     {
                        title: COLUMNS.ESTIMATED_DATE,
                        render: data =>
                           data.estimatedDate
                              ? convertDateTime({
                                   date: data.estimatedDate,
                                   customFormat: dateTimeFormat.americanFormat,
                                   dateOnly: true,
                                })
                              : "-",
                     },
                     {
                        title: COLUMNS.PRIORITY,
                        render: data => {
                           return (
                              <div className="highlighted-col text-capitalize">
                                 {data?.priority?.toLowerCase()}
                              </div>
                           )
                        },
                     },
                     {
                        title: COLUMNS.STATUS,
                        render: data => {
                           const currentStatus =
                              data.status == TimeLineOrderStatus.OPEN
                                 ? calculateTrailerStatus(data)
                                 : data.status
                           return (
                              <div
                                 className={`inv-req-popup-status min-badge-size ${TimeLineOrderStatus.getLabelClass(
                                    currentStatus
                                 )}`}
                              >
                                 {TimeLineOrderStatus.getDisplayTextKey(
                                    currentStatus
                                 )}
                              </div>
                           )
                        },
                     },
                     {
                        className: "text-center",
                        title: COLUMNS.ACTION,
                        render: data => {
                           return (
                              <div className="d-flex justify-content-center align-items-center ">
                                 <span
                                    className="text-success pointer tooltip-container"
                                    onClick={() =>
                                       handleNavigateToProductionSchedule(data)
                                    }
                                 >
                                    <img src={ActionEyeIcon} />
                                    <span className="custom-tooltip">View</span>
                                 </span>
                                 <span
                                    className=" pointer ms-3  tooltip-container"
                                    onClick={() => {
                                       setCurrentTrailerItem(data)
                                       setIsTrailerModalOpen(true)
                                    }}
                                    style={{
                                       pointerEvents: hasProductionSchedules
                                          ? "none"
                                          : "auto",
                                       opacity: hasProductionSchedules
                                          ? 0.5
                                          : 1,
                                    }}
                                 >
                                    <span className="custom-tooltip">Edit</span>
                                    <img src={EditIcon} />
                                 </span>

                                 <span
                                    className=" pointer ms-3  tooltip-container"
                                    onClick={() => handleInitDelete(data)}
                                 >
                                    <span className="custom-tooltip">
                                       Delete
                                    </span>
                                    <img src={DeleteIcon} />
                                 </span>
                              </div>
                           )
                        },
                     },
                  ]}
               />
               <Pagination
                  currentPage={page}
                  totalData={total}
                  onPageClick={handlePageClick}
                  rowLimit={pageSize}
                  onChangePageSize={onChangePageSize}
               />
            </CardBody>
         </Card>
         {isTrailerModalOpen && (
            <AddTrailerModal
               isDetailView={false}
               isOpen={isTrailerModalOpen}
               currentTrailerItem={currentTrailerItem}
               onSuccess={request}
               onToggleModal={() => {
                  setSelection({})
                  setCurrentTrailerItem(null)
                  setIsTrailerModalOpen(prev => !prev)
               }}
            />
         )}

         {/* Bulk Update Production Line Modal */}
         {isBulkUpdateModalOpen && (
            <BulkUpdateProductionLineModal
               isOpen={isBulkUpdateModalOpen}
               onClose={handleBulkUpdateModalClose}
               selectedTrailers={selectedTrailers}
               request={request}
               setSelectedTrailers={setSelectedTrailers}
               setIsSelectAll={setIsSelectAll}
            />
         )}
         <DeleteModal
            isOpen={openDelModal}
            toggleFunction={setOpenDelModal}
            id={delId}
            apiFunction={DeleteTimelineOrder}
            title={"Order"}
            requestFunction={() => {
               setDelId(null)
               request()
            }}
         />
      </div>
   )
}

export default AllTrailers
